import type { OAuth2Config, OAuth2FormValues } from '#layers/access/utils/auth-providers/protocols/oauth2';
import type { ValueOf } from '#layers/base/public';
import type { FormSchemaResolved } from '#layers/block-engine/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { AuthProtocol, defineAuthProvider } from '#layers/access/types/auth-providers/define';
import { oauth2 } from '#layers/access/utils/auth-providers/protocols/oauth2';
import { applyOAuth2PresetOptions, OIDC_CLAIM_MAPPINGS, OIDC_SCOPES, promoteOAuth2PresetFieldsBeforeCredentials } from './oauth2-preset';

interface GitLabFormValues extends Omit<OAuth2FormValues, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	instanceType: GitLabInstanceType;
	instanceUrl?: string;
}

const GITLAB_DOTCOM_URL = 'https://gitlab.com';

const GITLAB_INSTANCE_TYPE = {
	cloud: 'cloud',
	selfManaged: 'selfManaged',
} as const;

type GitLabInstanceType = ValueOf<typeof GITLAB_INSTANCE_TYPE>;

const GITLAB_DEFAULTS = {
	'scopes': OIDC_SCOPES,
	'claimMappings.sub': OIDC_CLAIM_MAPPINGS.sub,
	'claimMappings.email': OIDC_CLAIM_MAPPINGS.email,
} as const;

const GITLAB_APPLICATIONS_URL = 'https://docs.gitlab.com/integration/oauth_provider/';

const GITLAB_DESCRIPTIONS: Record<string, string> = {
	clientId: '$t:sso_providers.gitlab.client_id_hint',
	clientSecret: '$t:sso_providers.gitlab.client_secret_hint',
	instanceType: '$t:sso_providers.gitlab.instance_type_hint',
	instanceUrl: '$t:sso_providers.gitlab.instance_url_hint',
};

function normalizeGitLabOrigin(value: string | undefined): string {
	if (!value) return '';

	const trimmed = value.trim();
	if (!trimmed) return '';

	try {
		return new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`).origin;
	}
	catch {
		return trimmed.replace(/\/.*$/, '');
	}
}

function selectedGitLabInstanceUrl(instanceType: GitLabInstanceType | undefined, instanceUrl: string | undefined): string {
	if (instanceType === GITLAB_INSTANCE_TYPE.selfManaged) return instanceUrl ?? '';
	if (!instanceType && instanceUrl) return instanceUrl;

	return GITLAB_DOTCOM_URL;
}

function gitlabEndpoints(instanceUrl: string | undefined): Pick<OAuth2Config, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	const origin = normalizeGitLabOrigin(instanceUrl);

	if (!origin) {
		return {
			authorizationEndpoint: '',
			tokenEndpoint: '',
			userinfoEndpoint: '',
		};
	}

	return {
		authorizationEndpoint: `${origin}/oauth/authorize`,
		tokenEndpoint: `${origin}/oauth/token`,
		userinfoEndpoint: `${origin}/oauth/userinfo`,
	};
}

function parseGitLabInitialValues(config: unknown): Partial<GitLabFormValues> {
	const {
		authorizationEndpoint: _authorizationEndpoint,
		tokenEndpoint: _tokenEndpoint,
		userinfoEndpoint: _userinfoEndpoint,
		...parsed
	} = oauth2.parseInitialValues?.(config as OAuth2Config) ?? {};
	const authEndpoint = (config as { authorizationEndpoint?: unknown })?.authorizationEndpoint;

	if (typeof authEndpoint !== 'string') return parsed;

	let instanceUrl: string;

	try {
		instanceUrl = new URL(authEndpoint).origin;
	}
	catch {
		// Malformed URL in saved config — fall back to base parse so the form
		// renders the raw value rather than crashing on edit.
		return parsed;
	}

	if (instanceUrl === GITLAB_DOTCOM_URL) {
		return {
			...parsed,
			instanceType: GITLAB_INSTANCE_TYPE.cloud,
		};
	}

	return {
		...parsed,
		instanceType: GITLAB_INSTANCE_TYPE.selfManaged,
		instanceUrl,
	};
}

function applyGitLabDefaults(items: FormSchemaResolved<any>, values: Partial<GitLabFormValues>): FormSchemaResolved<any> {
	const selectedInstanceUrl = () => selectedGitLabInstanceUrl(values.instanceType, values.instanceUrl);

	const configured = applyOAuth2PresetOptions(items, {
		defaults: GITLAB_DEFAULTS,
		descriptions: GITLAB_DESCRIPTIONS,
		fieldOverrides: {
			authorizationEndpoint: {
				default: () => gitlabEndpoints(selectedInstanceUrl()).authorizationEndpoint || undefined,
				props: { type: 'url', readonly: true },
			},
			tokenEndpoint: {
				default: () => gitlabEndpoints(selectedInstanceUrl()).tokenEndpoint || undefined,
				props: { type: 'url', readonly: true },
			},
			userinfoEndpoint: {
				default: () => gitlabEndpoints(selectedInstanceUrl()).userinfoEndpoint || undefined,
				props: { type: 'url', readonly: true },
			},
		},
	});

	return promoteOAuth2PresetFieldsBeforeCredentials([
		...configured,
		{
			name: 'instanceType',
			label: '$t:sso_providers.gitlab.instance_type_label',
			description: '$t:sso_providers.gitlab.instance_type_hint',
			block: BuiltinBlockIds.InputTileRadio,
			required: true,
			default: GITLAB_INSTANCE_TYPE.cloud,
			props: {
				options: [
					{
						value: GITLAB_INSTANCE_TYPE.cloud,
						label: '$t:sso_providers.gitlab.instance_type_cloud_label',
						description: '$t:sso_providers.gitlab.instance_type_cloud_description',
						icon: 'logos:gitlab-icon',
					},
					{
						value: GITLAB_INSTANCE_TYPE.selfManaged,
						label: '$t:sso_providers.gitlab.instance_type_self_managed_label',
						description: '$t:sso_providers.gitlab.instance_type_self_managed_description',
						icon: 'lucide:server',
					},
				],
				autoTitleCaseValue: false,
			},
		},
		{
			name: 'instanceUrl',
			label: '$t:sso_providers.gitlab.instance_url_label',
			description: '$t:sso_providers.gitlab.instance_url_hint',
			block: BuiltinBlockIds.Input,
			required: true,
			hidden: () => values.instanceType !== GITLAB_INSTANCE_TYPE.selfManaged,
			default: GITLAB_DOTCOM_URL,
			props: { type: 'url', placeholder: '$t:sso_providers.gitlab.instance_url_placeholder' },
		},
	], ['instanceType', 'instanceUrl']);
}

export const gitlab = defineAuthProvider<typeof oauth2, OAuth2Config, GitLabFormValues>({
	id: 'gitlab',
	name: '$t:sso_providers.gitlab.name',
	description: '$t:sso_providers.gitlab.description',
	protocol: AuthProtocol.OAuth2,
	icon: 'logos:gitlab-icon',
	docsUrl: 'https://docs.gitlab.com/integration/oauth_provider/',
	walkthroughStepKeys: [
		{
			key: 'sso_providers.gitlab.walkthrough.create_app',
			href: GITLAB_APPLICATIONS_URL,
		},
		'sso_providers.gitlab.walkthrough.redirect',
		'sso_providers.gitlab.walkthrough.scopes',
		'sso_providers.gitlab.walkthrough.credentials',
	],
	extends: oauth2,
	options: ({ form, context, values }) =>
		form(applyGitLabDefaults(context.base?.resolvedOptions ?? [], values) as never),
	transformOptions: (form): OAuth2Config => {
		const {
			instanceType,
			instanceUrl,
			...rest
		} = form;

		return oauth2.transformOptions!({
			...rest,
			...gitlabEndpoints(selectedGitLabInstanceUrl(instanceType, instanceUrl)),
		});
	},
	parseInitialValues: parseGitLabInitialValues,
});
